import { useId } from "react"

import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import { Input } from "@coval/ui/input"
import { Label } from "@coval/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui/select"

import type { MetricSemanticType } from "../types"

interface MetricTypedValueInputProps {
  metricType: MetricSemanticType
  categoryOptions: string[]
  value: string
  onChange: (value: string) => void
  label?: string
  placeholder?: string
  ariaLabel?: string
  className?: string
}

const BINARY_VALUES = [
  ANNOTATION_VALUE.YES,
  ANNOTATION_VALUE.NO,
  ANNOTATION_VALUE.UNKNOWN,
]

/**
 * Renders the appropriate value input for a given metric semantic type:
 *  - binary                           → Select with YES / NO / UNKNOWN
 *  - categorical with options         → Select with category options
 *  - categorical without options      → text Input (graceful fallback)
 *  - numerical                        → number Input
 *  - untyped                          → text Input
 *
 * The caller owns operator selection and form state.
 */
export function MetricTypedValueInput({
  metricType,
  categoryOptions,
  value,
  onChange,
  label,
  placeholder,
  ariaLabel,
  className,
}: MetricTypedValueInputProps) {
  const id = useId()

  if (metricType === "binary") {
    return (
      <div className={className}>
        {label && <Label htmlFor={id}>{label}</Label>}
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger id={id} aria-label={ariaLabel ?? label ?? "Value"}>
            <SelectValue placeholder={placeholder ?? "Select value"} />
          </SelectTrigger>
          <SelectContent>
            {BINARY_VALUES.map((v) => (
              <SelectItem key={v} value={v}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }

  if (metricType === "categorical" && categoryOptions.length > 0) {
    return (
      <div className={className}>
        {label && <Label htmlFor={id}>{label}</Label>}
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger id={id} aria-label={ariaLabel ?? label ?? "Value"}>
            <SelectValue placeholder={placeholder ?? "Select value"} />
          </SelectTrigger>
          <SelectContent>
            {categoryOptions.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }

  const isNumeric = metricType === "numerical"
  return (
    <div className={className}>
      {label && <Label htmlFor={id}>{label}</Label>}
      <Input
        id={id}
        type={isNumeric ? "number" : "text"}
        step={isNumeric ? "0.01" : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder ?? (isNumeric ? "Number" : "Text value")}
        aria-label={ariaLabel ?? label ?? "Value"}
      />
    </div>
  )
}
